// CRM-iframe profile harvest: when the parent SPA posts `tg:harvestUser`
// ({ peerId }) — after the operator creates a lead from the open chat — read the
// peer's profile (selectUser + selectUserFullInfo) from THIS already-connected
// session's in-memory state and post it back as `tg:userHarvested`. The CRM
// merges it into the contact WITHOUT opening a second GramJS session on the
// account (the documented anti-ban win). Mirrors crmOpenChat's inbound pattern.

import { getActions, getGlobal } from '../global';
import { selectChat, selectUser, selectUserFullInfo } from '../global/selectors';
import { isUserBot } from '../global/helpers';

import { getCrmOrigin } from './crmBridge';

const FULLINFO_POLL_INTERVAL_MS = 200;
const FULLINFO_TIMEOUT_MS = 4000;

let isListening = false;

// fullInfo (bio/birthday/business/personal-channel) is loaded lazily. The
// operator just had this chat open, so it's usually already cached; if not,
// kick loadFullUser and poll briefly for it. We harvest whatever's present
// when we give up — the basic ApiUser fields are enough to be useful.
function whenFullInfoReady(peerId: string): Promise<void> {
  return new Promise((resolve) => {
    if (selectUserFullInfo(getGlobal(), peerId)) {
      resolve();
      return;
    }
    try {
      getActions().loadFullUser({ userId: peerId });
    } catch {
      // action registry not ready — fall through to the poll/timeout
    }
    const startedAt = Date.now();
    const check = () => {
      if (selectUserFullInfo(getGlobal(), peerId) || Date.now() - startedAt > FULLINFO_TIMEOUT_MS) {
        resolve();
        return;
      }
      setTimeout(check, FULLINFO_POLL_INTERVAL_MS);
    };
    setTimeout(check, FULLINFO_POLL_INTERVAL_MS);
  });
}

function buildHarvest(peerId: string): Record<string, unknown> | null {
  const global = getGlobal();
  const user = selectUser(global, peerId);
  if (!user) return null;
  const full = selectUserFullInfo(global, peerId);

  const activeUsername =
    user.usernames?.find((u) => u.isActive)?.username
    ?? user.usernames?.[0]?.username
    ?? null;

  const out: Record<string, unknown> = {
    telegramId: peerId,
    hasUserObj: true, // we have the User object in state — flags are authoritative
    // The per-account access_hash IS exposed on ApiUser (buildApiUser copies it
    // off the MTProto user). Ship it so the CRM can cache (account,contact) peer
    // access — that's what lets a later worker enrich / backfill resolve this
    // peer by its bare numeric id (no @username, no fresh resolve). int64, may be
    // negative; the server validates the format before storing.
    accessHash: user.accessHash ?? null,
    username: activeUsername,
    firstName: user.firstName ?? null,
    lastName: user.lastName ?? null,
    phone: user.phoneNumber ? `+${user.phoneNumber}` : null,
    isPremium: !!user.isPremium,
    isVerified: !!user.isVerified,
    isBot: isUserBot(user),
    isScam: user.fakeType === 'scam',
    isFake: user.fakeType === 'fake',
  };

  if (full) {
    if (full.bio) out.bio = full.bio;
    if (full.birthday) {
      out.birthdayDay = full.birthday.day;
      out.birthdayMonth = full.birthday.month;
      if (full.birthday.year != null) out.birthdayYear = full.birthday.year;
    }
    if (full.personalChannelId) {
      out.personalChannelId = full.personalChannelId;
      const ch = selectChat(global, full.personalChannelId);
      if (ch) {
        if (ch.title) out.personalChannelTitle = ch.title;
        const chUsername = ch.usernames?.find((u) => u.isActive)?.username ?? ch.usernames?.[0]?.username;
        if (chUsername) out.personalChannelUsername = chUsername;
      }
    }
    if (full.businessLocation?.address) out.businessLocation = full.businessLocation.address;
    if (full.businessIntro?.title) out.businessIntroTitle = full.businessIntro.title;
    if (full.businessIntro?.description) out.businessIntroText = full.businessIntro.description;
  }

  return out;
}

// Start listening for `tg:harvestUser` from the trusted parent. Same
// origin/source allow-list as the other CRM bridges. Idempotent.
export function listenForCrmHarvest() {
  if (isListening) return;
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  isListening = true;

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.origin !== origin) return;
    if (event.source !== window.parent) return;
    const data: any = event.data;
    if (!data || data.type !== 'tg:harvestUser') return;

    const peerId = data.payload?.peerId;
    if (typeof peerId !== 'string' || !peerId) return;

    void (async () => {
      await whenFullInfoReady(peerId);
      const fields = buildHarvest(peerId);
      if (!fields) return; // peer not in state — nothing to harvest
      try {
        window.parent.postMessage({ type: 'tg:userHarvested', payload: { peerId, fields } }, origin);
      } catch {
        // parent went away — best effort only
      }
    })();
  });
}
